/**
 * The chart settings popup (screenshots/72 Chart tab, 76 Data, 77 Labels, 78 Colors, 79 Interact; behaviors in 81).
 * Left: five tabs that edit one `ChartSettings` through the store (undo/redo, autosave drafts).
 * Right: the live widget at S/M/L, rendered by ChartWidget with the effective settings (hover previews included).
 *
 * The parent owns data fetching: it watches `settings.data` (bindingToQuery) and passes the returned Shape + rows.
 * Tabs live in ./settings/*. Keyboard: ←/→ between tabs, ⌘Z / ⇧⌘Z, ⌘↵ saves, Esc closes (after any open popover).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Switch } from "../../components/shared/ui";
import { toastStore } from "../../stores/toastStore";
import { CIcon } from "./icons";
import { CHART_TYPES, type Shape } from "./chartTypes";
import { DATASETS, acceptsField, bindingProblems, type ChartSettings, type Field, type Group } from "./chartSettings";
import { effectiveSettings, useChartSettings, type ChartSettingsStore, type SettingsTab } from "./chartSettingsStore";
import type { ChartTheme } from "./themes";
import type { DatasetResult, LayoutItem } from "../types";
import { entryOf } from "../catalog";
import { ChartTab } from "./settings/ChartTab";
import { ColorsTab } from "./settings/ColorsTab";
import { DataTab } from "./settings/DataTab";
import { InteractTab } from "./settings/InteractTab";
import { LabelsTab, labelChanges, labelRows, type Pulse } from "./settings/LabelsTab";
import { PreviewPane } from "./settings/PreviewPane";
import { renameKeyOf } from "./settings/PreviewExtras";

const TABS: { id: SettingsTab; label: string; icon: string }[] = [
  { id: "data", label: "Data", icon: "database" }, { id: "chart", label: "Chart", icon: "chart-column" },
  { id: "labels", label: "Labels", icon: "type" }, { id: "colors", label: "Colors", icon: "palette" }, { id: "interact", label: "Interact", icon: "wand-sparkles" },
];

export interface ChartSettingsDialogProps {
  store: ChartSettingsStore;
  shape: Shape;
  loading?: boolean;
  /** Extras per row for tokens ({ward}, …). */
  vars?: Record<string, Record<string, string | number>>;
  /** The dataset answer behind `shape`: query results, timing, totals. */
  result?: DatasetResult | null;
  group: Group;
  themes: ChartTheme[];
  canSaveForTeam: boolean;          // can(group, role, "teamViews")
  mode: "add" | "edit";
  /** Other widgets on the overview a click on `field` filters (79). */
  crossTargets?: (field: string | undefined) => LayoutItem[];
  onSave: (s: ChartSettings, o: { forTeam: boolean }) => void;
  onClose: () => void;
  onOpenThemes: () => void;
}

export function ChartSettingsDialog(p: ChartSettingsDialogProps) {
  const { store } = p;
  const tab = useChartSettings(store, (s) => s.tab);
  const settings = useChartSettings(store, (s) => s.settings);
  const preview = useChartSettings(store, (s) => s.preview);
  const effective = useMemo(() => effectiveSettings({ settings, preview }), [settings, preview]);
  const canUndo = useChartSettings(store, (s) => s.past.length > 0);
  const canRedo = useChartSettings(store, (s) => s.future.length > 0);
  const [forTeam, setForTeam] = useState(false);
  const [held, setHeld] = useState<Field | null>(null);
  const [pulse, setPulse] = useState<Pulse>(null);
  const [wide, setWide] = useState(false);
  const [announce, setAnnounce] = useState("");
  const initial = useRef(settings);
  const root = useRef<HTMLDivElement>(null);
  const theme = p.themes.find((t) => t.id === effective.themeId);
  const problems = bindingProblems(settings.data);
  const ds = DATASETS[settings.data.datasetId];
  const unit = "unit" in p.shape && p.shape.unit ? p.shape.unit : "count";
  const rows = labelRows(p.shape);
  const changes = labelChanges(initial.current, settings, unit, rows).map((c) => ({ ...c, undo: () => store.getState().update(c.revert, `Undo: ${c.what}`) }));
  const targets = (p.crossTargets?.(settings.data.mapping.category) ?? []).map((i) => ({ title: i.config.title ?? entryOf(i.type).title, icon: entryOf(i.type).icon, type: i.type }));
  const save = () => { if (!bindingProblems(store.getState().settings.data).length) p.onSave(store.getState().settings, { forTeam }); };

  useEffect(() => { root.current?.querySelector<HTMLElement>(`#cs-tab-${store.getState().tab}`)?.focus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const typing = (e.target as HTMLElement).closest?.("input, select, textarea");
      if (mod && e.key.toLowerCase() === "z" && !typing) { e.preventDefault(); (e.shiftKey ? store.getState().redo : store.getState().undo)(); setAnnounce(e.shiftKey ? "Redone" : "Undone"); }
      else if (mod && e.key === "Enter") { e.preventDefault(); save(); }
      else if (e.key === "Escape" && !e.defaultPrevented) { if (store.getState().preview) store.getState().setPreview(null); else p.onClose(); }
      else if (e.key === "Tab" && root.current) {            // keep focus inside the dialog
        const f = [...root.current.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select, [tabindex='0']")].filter((x) => x.offsetParent);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f.at(-1)!.focus(); }
        else if (!e.shiftKey && document.activeElement === f.at(-1)) { e.preventDefault(); f[0].focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [forTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  const doPulse = (field: string) => {
    store.getState().setTab("labels");
    setPulse((x) => ({ field, n: (x?.n ?? 0) + 1 }));
    requestAnimationFrame(() => document.querySelector<HTMLElement>(".chart-settings [data-pulse]")?.scrollIntoView({ block: "nearest", behavior: "smooth" }));
  };

  return (
    <div ref={root} className={`chart-settings${wide ? " is-wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="chart-settings-title">
      {/* Phones (91): the popup is a bottom sheet with Cancel · Edit chart · Save. */}
      <div className="cs-mhead"><button type="button" className="link-button" onClick={p.onClose}>Cancel</button><strong>Edit chart</strong>
        <button type="button" className="link-button" disabled={problems.length > 0} onClick={save}>Save</button></div>
      <header className="chart-settings__head">
        <span className="chart-settings__icon"><CIcon name={CHART_TYPES[effective.type].icon} size={18} /></span>
        <div className="chart-settings__title">
          <InlineText id="chart-settings-title" value={settings.labels.title ?? "Untitled chart"} label="Chart title"
            onCommit={(v) => store.getState().update((s) => ({ ...s, labels: { ...s.labels, title: v } }), "Title")} />
          <span className="chart-settings__sub">Chart widget · {ds.label} · edited by you</span>
        </div>
        <SaveChip store={store} />
        <button type="button" className="icon-btn" aria-label="Undo" disabled={!canUndo} onClick={() => store.getState().undo()}><CIcon name="undo-2" size={16} /></button>
        <button type="button" className="icon-btn" aria-label="Redo" disabled={!canRedo} onClick={() => store.getState().redo()}><CIcon name="redo-2" size={16} /></button>
        <ThemeMenu store={store} themes={p.themes} onOpenThemes={p.onOpenThemes} />
        <button type="button" className="icon-btn" aria-label="Close" onClick={p.onClose}><CIcon name="x" size={18} /></button>
      </header>

      <div className="chart-settings__body">
        <section className="chart-settings__panel">
          <div className="chart-settings__tabs" role="tablist" aria-label="Chart settings">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="tab" id={`cs-tab-${t.id}`} aria-selected={tab === t.id} aria-controls="cs-panel"
                tabIndex={tab === t.id ? 0 : -1} className="chart-settings__tab" onClick={() => store.getState().setTab(t.id)}
                onKeyDown={(e) => { const i = TABS.findIndex((x) => x.id === t.id); const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
                  if (d) { e.preventDefault(); const n = TABS[(i + d + TABS.length) % TABS.length]; store.getState().setTab(n.id); document.getElementById(`cs-tab-${n.id}`)?.focus(); } }}>
                <CIcon name={t.icon} size={15} />{t.label}
                {t.id === "labels" && changes.length > 0 && <span className="cs-count">{changes.length}</span>}
              </button>
            ))}
          </div>
          <div className="chart-settings__content" role="tabpanel" id="cs-panel" aria-labelledby={`cs-tab-${tab}`} key={tab}>
            {tab === "data" && <DataTab store={store} group={p.group} problems={problems} held={held} setHeld={setHeld} />}
            {tab === "chart" && <ChartTab store={store} shape={p.shape} sample={p.result ? !!p.result.sample : DATASETS[settings.data.datasetId].sample} theme={theme} onAnnounce={setAnnounce} />}
            {tab === "labels" && <LabelsTab store={store} shape={p.shape} vars={p.vars} pulse={pulse} />}
            {tab === "colors" && <ColorsTab store={store} shape={p.shape} theme={theme} onOpenThemes={p.onOpenThemes} />}
            {tab === "interact" && <InteractTab store={store} alsoFilters={targets} />}
          </div>
        </section>

        <PreviewPane tab={tab} settings={effective} committed={settings} shape={p.shape} result={p.result ?? null} loading={p.loading} vars={p.vars} theme={theme}
          dropHint={!!held && acceptsField("split", held).ok} changes={changes} crossTargets={targets} wide={wide} onWide={() => setWide((v) => !v)}
          onTitle={(v) => store.getState().update((s) => ({ ...s, labels: { ...s.labels, title: v } }), "Title")}
          onRename={(label, v) => { const key = renameKeyOf(p.shape, label); store.getState().update((s) => ({ ...s, labels: { ...s.labels, renames: { ...s.labels.renames, [key]: v } } }), `Renamed ${label}`); }}
          onPulse={(field) => doPulse(field.startsWith("rename:") ? `rename:${renameKeyOf(p.shape, field.slice(7))}` : field)} />
      </div>

      <footer className="chart-settings__foot">
        {p.canSaveForTeam && <><Switch on={forTeam} onChange={setForTeam} label="Save as team chart" /><span className="chart-settings__team">Save as team chart</span>
          <span className="cs-admins"><CIcon name="shield" size={12} />Admins</span></>}
        <span className="spacer" />
        {problems.length > 0 && <span className="chart-settings__problem" role="status">{problems[0]}</span>}
        <button type="button" className="btn btn--secondary" onClick={p.onClose}>Cancel</button>
        <button type="button" className="btn btn--primary" disabled={problems.length > 0} onClick={save}>
          <CIcon name={p.mode === "add" ? "plus" : "check"} />{p.mode === "add" ? "Add to overview" : "Save chart"}</button>
      </footer>
      <span className="cs-sr" role="status" aria-live="polite">{announce}</span>
    </div>
  );
}

// ---------------------------------------------------------------- header bits

function SaveChip({ store }: { store: ChartSettingsStore }) {
  const save = useChartSettings(store, (s) => s.save);
  const text = { idle: "", saving: "Saving…", saved: "Draft saved", error: "Couldn’t save draft" }[save];
  return <span className={`save-chip save-chip--${save}`} role="status" aria-live="polite" key={save}>
    {save === "saving" ? <CIcon name="loader" size={13} className="cs-spin" /> : save === "saved" ? <CIcon name="cloud-check" size={13} /> : null}{text}</span>;
}

/** Hovering a theme previews it on the widget (store preview → ChartWidget → chart.setTheme); choosing it shows Undo (81 #7). */
function ThemeMenu({ store, themes, onOpenThemes }: { store: ChartSettingsStore; themes: ChartTheme[]; onOpenThemes: () => void }) {
  const [open, setOpen] = useState(false);
  const current = useChartSettings(store, (s) => s.settings.themeId);
  const cur = themes.find((t) => t.id === current);
  const list = useRef<HTMLUListElement>(null);
  const choose = (t: ChartTheme) => {
    setOpen(false);
    if (t.id === current) { store.getState().setPreview(null); return; }
    store.getState().update((s) => ({ ...s, themeId: t.id }), `Theme: ${t.name}`);
    toastStore.getState().show({ message: `Applied ${t.name}`, actionLabel: "Undo", onAction: () => store.getState().undo() });
  };
  useEffect(() => { if (open) list.current?.querySelector<HTMLElement>("[aria-selected='true']")?.focus(); }, [open]);
  return (
    <div className="theme-menu" onMouseLeave={() => { if (open) store.getState().setPreview(null); }}>
      <button type="button" className="theme-pill" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <CIcon name="palette" /><span className="theme-pill__k">Theme</span>
        <span className="theme-dots">{cur?.palette.slice(0, 3).map((c) => <i key={c} style={{ background: c }} />)}</span>{cur?.name ?? current}<CIcon name="chevron-down" size={13} />
      </button>
      {open && (
        <ul ref={list} role="listbox" className="theme-menu__list" aria-label="Chart theme"
          onKeyDown={(e) => {
            const items = [...(list.current?.querySelectorAll<HTMLElement>("li") ?? [])];
            const i = items.indexOf(document.activeElement as HTMLElement);
            if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus(); }
            if (e.key === "Escape") { e.preventDefault(); store.getState().setPreview(null); setOpen(false); }
          }}>
          {themes.map((t) => (
            <li key={t.id} role="option" aria-selected={t.id === current} tabIndex={-1}
              onMouseEnter={() => store.getState().setPreview({ themeId: t.id })} onFocus={() => store.getState().setPreview({ themeId: t.id })}
              onClick={() => choose(t)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choose(t); } }}>
              <span className="theme-dots">{t.palette.slice(0, 3).map((c) => <i key={c} style={{ background: c }} />)}</span>{t.name}
            </li>
          ))}
          <li role="option" aria-selected={false} tabIndex={-1} className="theme-menu__edit" onClick={onOpenThemes} onKeyDown={(e) => { if (e.key === "Enter") onOpenThemes(); }}>Edit themes…</li>
        </ul>
      )}
    </div>
  );
}

/** Click-to-edit text: ↵ saves, Esc cancels. Used for the dialog title. */
export function InlineText({ id, value, label, onCommit }: { id?: string; value: string; label: string; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  if (!editing) return <button type="button" id={id} className="inline-text" onClick={() => setEditing(true)} aria-label={`${label}: ${value}. Edit`}>{value}<CIcon name="pencil" size={14} /></button>;
  return <input className="inline-text__input" aria-label={label} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
    onBlur={() => { setEditing(false); if (draft.trim() && draft !== value) onCommit(draft.trim()); }}
    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { e.preventDefault(); setDraft(value); setEditing(false); } }} />;
}
